import { type FC } from 'react';
import * as Yup from 'yup';

import { FormModal } from '../../../components/Formik';
import allowedDomains from '../../../helpers/allowedEmailDomains';

import type { FormModalProps } from '@/types/formTypes';

declare global {
  interface Window {
    PORTAL_ENV?: string;
  }
}

interface CreateUserFormModalProps {
  roles: Record<string, string>;
  onSubmit: FormModalProps['onSubmit'];
  onCancel: () => void;
}

const CreateUserFormModal: FC<CreateUserFormModalProps> = ({ roles, onSubmit, onCancel }) => {
  const fields: FormModalProps['fields'] = [
    {
      fieldType: 'text',
      name: 'email',
      label: 'Email',
    },
    {
      fieldType: 'select',
      name: 'role',
      label: 'Role',
      menuItems: Object.keys(roles).map(key => ({
        value: key,
        label: roles[key] ?? '',
      })),
    },
  ];

  const initialValues = {
    email: '',
    role: '',
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required('Required')
      .email('Invalid email address')
      .test('allowed-domain', 'Invalid email address', (value: string | undefined) => {
        if (!value) return false;
        // Check if the environment is production or PORTAL_ENV is set
        if (window.PORTAL_ENV === 'production' || import.meta.env.VITE_ENV === 'production') {
          const domain = value.split('@')[1];
          return allowedDomains.includes(domain ?? '');
        }
        return true; // In development, allow any email
      }),
    role: Yup.string().required('Required'),
  });

  return (
    <FormModal
      title='Create New User'
      fields={fields}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      onCancel={onCancel}
      submitLabel='Create'
      width='444px'
    />
  );
};

export default CreateUserFormModal;
